import config from '../config';
import { getValidAccessToken, refreshAccessToken } from './session';

/**
 * Client for the Insight AI backend (zelp-org/zelp-ai-poc).
 *
 * Deliberately not the app's `requester`: that adds an `x-api-key` header the AI
 * backend's CORS policy doesn't allow (the preflight fails), and it reads whole
 * response bodies, while a chat reply is a stream. Auth is the same though — the
 * backend verifies our access token against the main API's /token/check.
 */

const baseURL = () => (config.aiApiURL || '').replace(/\/+$/, '');

export const isAIChatConfigured = () => !!baseURL();

export class AIChatApiError extends Error {
    constructor(message, status = null) {
        super(message);
        this.name = 'AIChatApiError';
        this.status = status;
    }
}

const isAbortError = err => err?.name === 'AbortError';

const NETWORK_ERROR = 'Couldn\'t reach Insight AI. Check your connection and try again.';
const GENERIC_ERROR = 'Something went wrong with Insight AI. Please try again.';

/**
 * fetch with the access token, renewed once on a 401.
 *
 * A 401 that survives the refresh is not treated as the end of the session: the
 * main API has just accepted (or issued) that token, so the AI backend rejecting it
 * means it's checking against a different environment — the user should stay
 * signed in to the rest of the app.
 */
const authedFetch = async (url, options = {}) => {
    const token = await getValidAccessToken();
    if (!token) throw new AIChatApiError('Your session has ended. Sign in again to use Insight AI.', 401);

    const send = accessToken => fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });

    let response = await send(token);
    if (response.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed) response = await send(refreshed);
    }
    return response;
};

// The backend's error bodies are { error: string } and written for users
// (unsupported file type, file too large, ...), so they're shown as they are.
const errorFromResponse = async response => {
    if (response.status === 401) {
        return new AIChatApiError('Insight AI couldn\'t verify your session. Please try again later.', 401);
    }
    let message = null;
    try {
        message = (await response.json())?.error;
    }
    catch (e) {
        // not JSON
    }
    return new AIChatApiError(typeof message === 'string' && message ? message : GENERIC_ERROR, response.status);
};

const request = async (url, options) => {
    let response;
    try {
        response = await authedFetch(url, options);
    }
    catch (err) {
        if (err instanceof AIChatApiError || isAbortError(err)) throw err;
        throw new AIChatApiError(NETWORK_ERROR);
    }
    if (!response.ok) throw await errorFromResponse(response);
    return response;
};

/**
 * Incremental parser for a text/event-stream body. `push` takes decoded text in
 * whatever chunks the network delivers and calls `onEvent({ event, data })` once per
 * complete event, with `data` JSON-parsed (the backend always sends JSON).
 */
export const createSseParser = onEvent => {
    let buffer = '';

    const dispatch = block => {
        let event = 'message';
        const dataLines = [];
        block.split(/\r?\n/).forEach(line => {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
        });
        if (!dataLines.length) return;
        let data;
        try {
            data = JSON.parse(dataLines.join('\n'));
        }
        catch (e) {
            return;
        }
        onEvent({ event, data });
    };

    return {
        push(text) {
            buffer += text;
            const blocks = buffer.split(/\r?\n\r?\n/);
            buffer = blocks.pop();
            blocks.forEach(dispatch);
        },
        flush() {
            if (buffer.trim()) dispatch(buffer);
            buffer = '';
        },
    };
};

/**
 * The backend ends every answer with a debug line ("Completed in 12.3 secs. Input
 * Tokens: ... Estimated Cost: $...") sent as its own text event. It isn't meant for
 * customers and isn't in the stored history, so it's dropped here.
 */
export const isUsageFooter = text => typeof text === 'string'
    && /^\s*Completed in [\d.]+ secs\.\s+Input Tokens:/.test(text);

/**
 * Asks a question and streams the answer. Resolves when the stream ends; events
 * arrive through `onEvent`: `text` (a chunk of the answer), `chart` ({ filename,
 * mimeType, data: base64 }), `done` and `error` ({ message }).
 * Rejects with AIChatApiError when the request is refused before streaming starts.
 */
export const streamChat = async ({
    question, files = [], farmId, signal, onEvent
}) => {
    const body = new FormData();
    body.append('question', question);
    files.forEach(file => body.append('files', file, file.name));
    if (farmId) body.append('farmId', String(farmId));

    const response = await request(`${baseURL()}/chat`, { method: 'POST', body, signal });

    const parser = createSseParser(onEvent);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done) break;
            parser.push(decoder.decode(value, { stream: true }));
        }
        parser.push(decoder.decode());
        parser.flush();
    }
    catch (err) {
        if (isAbortError(err)) throw err;
        // the connection dropped mid-answer; the caller sees a stream with no `done`
    }
};

/**
 * The stored conversation (the backend's last 20 messages):
 * [{ id, role, text, attachments?: [{ filename, mimeType }], charts?: [{ url, filename }] }].
 */
export const getHistory = async ({ signal } = {}) => {
    const response = await request(`${baseURL()}/history`, { signal });
    return (await response.json())?.history || [];
};

/** This week's AI spend for a farm: { spentUsd, budgetUsd, resetsAt }. */
export const getUsage = async ({ farmId, signal } = {}) => {
    const query = farmId ? `?farmId=${encodeURIComponent(farmId)}` : '';
    const response = await request(`${baseURL()}/usage${query}`, { signal });
    return response.json();
};
